import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { FetcherObject } from "./helpers/fetcherSettingsDummyData";
import { isDeprecatedKey, isUnionKey } from "./helpers/fetcherSettingsResolver";
import ObjectRowForm, { ObjectDraft } from "./ObjectRowForm";

interface Props {
  /** "This report" / "This interface" */
  ownLabel: string;
  ownObjects: FetcherObject[];
  /** absent for an interface - nothing sits above it */
  inheritedLabel?: string;
  inheritedObjects?: FetcherObject[];
  markedForDelete: string[];
  readOnly?: boolean;
  onAdd: (fieldType: string, fieldValue: string) => void;
  onEdit: (id: string, fieldType: string, fieldValue: string) => void;
  onMarkDelete: (id: string) => void;
  onUndoDelete: (id: string) => void;
}

const byKeyThenValue = (a: FetcherObject, b: FetcherObject) =>
  a.k.localeCompare(b.k) || a.v.localeCompare(b.v);

const truncate = (value: string, max = 160) =>
  value.length > max ? `${value.slice(0, max)}…` : value;

const ColumnHeader = ({
  label,
  count,
  hint,
}: {
  label: string;
  count: number;
  hint: string;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{
      px: 1.5,
      py: 1,
      borderBottom: "1px solid #0000001F",
      backgroundColor: "#FAFAFC",
    }}
  >
    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#00000099" }}>
      {label}
    </Typography>
    <Chip size="small" label={count} />
    <Box sx={{ flex: 1 }} />
    <Typography sx={{ fontSize: "12px", color: "#00000099" }}>{hint}</Typography>
  </Stack>
);

const ObjectValue = ({ value }: { value: string }) => (
  <Typography
    sx={{
      fontFamily: "monospace",
      fontSize: "12px",
      wordBreak: "break-all",
    }}
  >
    {truncate(value)}
  </Typography>
);

/**
 * The objects editor used by the report and interface editors: what the level
 * above already sets (read only) beside the rows this level owns.
 */
const ObjectsPanel = ({
  ownLabel,
  ownObjects,
  inheritedLabel,
  inheritedObjects = [],
  markedForDelete,
  readOnly = false,
  onAdd,
  onEdit,
  onMarkDelete,
  onUndoDelete,
}: Props) => {
  const [draft, setDraft] = useState<ObjectDraft | null>(null);

  const own = [...ownObjects].sort(byKeyThenValue);
  const inherited = [...inheritedObjects].sort(byKeyThenValue);
  const ownKeys = new Set(own.map((row) => row.k));

  const commitDraft = () => {
    if (!draft || !draft.k) return;
    if (draft.id) onEdit(draft.id, draft.k, draft.v);
    else onAdd(draft.k, draft.v);
    setDraft(null);
  };

  const keyCount = (key: string) => own.filter((row) => row.k === key).length;

  return (
    <Paper variant="outlined" sx={{ mt: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1.5, borderBottom: "1px solid #0000001F" }}
      >
        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
          Objects
        </Typography>
        <Box sx={{ flex: 1 }} />
        {!readOnly && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => setDraft({ k: "", v: "", builder: false })}
          >
            Add object
          </Button>
        )}
      </Stack>

      <Stack direction={inheritedLabel ? "row" : "column"}>
        {inheritedLabel && (
          <Box
            sx={{
              width: "50%",
              borderRight: "1px solid #0000001F",
              minWidth: 0,
            }}
          >
            <ColumnHeader
              label={inheritedLabel}
              count={inherited.length}
              hint="read only"
            />
            <Box sx={{ p: 1.5, maxHeight: 340, overflowY: "auto" }}>
              {inherited.length === 0 ? (
                <Typography
                  sx={{
                    fontSize: "12.5px",
                    color: "#00000099",
                    textAlign: "center",
                    py: 3,
                  }}
                >
                  Nothing set at interface level.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {inherited.map((row) => {
                    const overridden = ownKeys.has(row.k) && !isUnionKey(row.k);
                    return (
                      <Box
                        key={row.id}
                        sx={{
                          border: "1px solid #0000001F",
                          borderRadius: "7px",
                          p: 1,
                          opacity: overridden ? 0.55 : 1,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography
                            sx={{ fontSize: "12.5px", fontWeight: 500 }}
                          >
                            {row.k}
                          </Typography>
                          {isUnionKey(row.k) && (
                            <Chip size="small" label="UNION" color="secondary" />
                          )}
                        </Stack>
                        {overridden && (
                          <Typography
                            sx={{ fontSize: "11.5px", color: "#B45309" }}
                          >
                            overridden here
                          </Typography>
                        )}
                        <ObjectValue value={row.v} />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ColumnHeader
            label={ownLabel}
            count={own.length}
            hint={readOnly ? "read only" : "editable"}
          />
          <Box sx={{ p: 1.5, maxHeight: 340, overflowY: "auto" }}>
            {draft && (
              <ObjectRowForm
                draft={draft}
                onChange={setDraft}
                onCommit={commitDraft}
                onCancel={() => setDraft(null)}
              />
            )}

            {own.length === 0 ? (
              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: "#00000099",
                  textAlign: "center",
                  py: 3,
                }}
              >
                No objects yet.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {own.map((row, index) => {
                  const marked = markedForDelete.includes(row.id);
                  const showGroupHeader =
                    (index === 0 || own[index - 1].k !== row.k) &&
                    keyCount(row.k) > 1;
                  return (
                    <Box key={row.id}>
                      {showGroupHeader && (
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: "#00000099",
                            fontWeight: 700,
                            mb: 0.5,
                          }}
                        >
                          {row.k} × {keyCount(row.k)}
                          {isUnionKey(row.k) ? " — all sent" : ""}
                        </Typography>
                      )}
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={1}
                        sx={{
                          border: "1px solid #0000001F",
                          borderRadius: "7px",
                          p: 1,
                          backgroundColor: marked ? "#FBE9E9" : "transparent",
                          textDecoration: marked ? "line-through" : "none",
                        }}
                      >
                        <Box sx={{ width: 190, flex: "none" }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Typography
                              sx={{ fontSize: "12.5px", fontWeight: 500 }}
                            >
                              {row.k}
                            </Typography>
                            {isUnionKey(row.k) && (
                              <Chip
                                size="small"
                                label="UNION"
                                color="secondary"
                              />
                            )}
                          </Stack>
                          {isDeprecatedKey(row.k) && (
                            <Typography
                              sx={{ fontSize: "11.5px", color: "#C0392B" }}
                            >
                              deprecated
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <ObjectValue value={row.v} />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "11.5px",
                            color: "#00000099",
                            width: 52,
                            flex: "none",
                            textAlign: "right",
                          }}
                        >
                          {row.id}
                        </Typography>
                        {!readOnly && (
                          <Stack direction="row" sx={{ flex: "none" }}>
                            {marked ? (
                              <Tooltip title="Undo delete">
                                <IconButton
                                  size="small"
                                  onClick={() => onUndoDelete(row.id)}
                                >
                                  <UndoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setDraft({
                                        id: row.id,
                                        k: row.k,
                                        v: row.v,
                                        builder: false,
                                      })
                                    }
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Stage delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => onMarkDelete(row.id)}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
};

export default ObjectsPanel;
